import { DataTable, useColumns } from "@/components/data-table";
import { DataFetcher } from "@/components/display";
import { ListPage } from "@/components/list-page";
import { ConfirmDialog, Drawer, ModalHeader, ModalValueExtractor, useOpenModal } from "@/components/modal";
import useHasPermisos from "@/features/auth/hooks/useHasPermisos";
import { AddDepositoMutation } from "@/features/inventario/depositos/api/add-deposito";
import { DeleteDepositoMutation } from "@/features/inventario/depositos/api/delete-deposito";
import { EditDepositoMutation } from "@/features/inventario/depositos/api/edit-deposito";
import { useGetAllDepositosQuery } from "@/features/inventario/depositos/api/get-all-depositos";
import { GetDepositoQuery } from "@/features/inventario/depositos/api/get-deposito";
import DepositoForm, { depositoFormSchema } from "@/features/inventario/depositos/components/deposito-form";
import { formatDate, formatString } from "@/shared/formats";
import { notify } from "@/shared/notification";
import { Permiso } from "@/shared/types/permiso";
import { useFilters } from "@/components/filters";
import { SchemaFormHook } from "@/components/form";

export default function DepositosPage() {
    const openModal = useOpenModal();

    const hasWritePermiso = useHasPermisos(Permiso.WriteDepositos);

    const filterData = useFilters({
        filters: [
            { type: "string", label: "Nombre", name: "nombre" },
            { type: "pagination" }
        ]
    });

    const fetchData = useGetAllDepositosQuery(filterData.filterData);

    const columnData = useColumns({
        columns: [
            { key: "nombre", header: "Nombre", render: row => row.nombre },
            { key: "principal", header: "Principal", render: row => row.principal ? "Sí" : "No", align: "center" },
            { key: "creadoPor", header: "Creado Por", render: row => formatString(row.creadoPor), show: false },
            { key: "fechaCreacion", header: "Fecha de Creación", render: row => formatDate(row.fechaCreacion), align: "center", show: false },
            { key: "modificadoPor", header: "Modificado Por", render: row => formatString(row.modificadoPor), show: false },
            { key: "fechaModificacion", header: "Última Modificación", render: row => formatDate(row.fechaModificacion), align: "center", show: false },
        ],
        key: "depositos"
    });

    return <ListPage>
        <ListPage.Header>
            <ListPage.Header.Actions title="Depósitos">
                <ListPage.Header.Actions.FilterButton />
                <ListPage.Header.Actions.ColumnButton columnData={columnData} />
                {hasWritePermiso && <ListPage.Header.Actions.AddButton onClick={() => openModal("add")} />}
            </ListPage.Header.Actions>
            <ListPage.Header.Filters filterData={filterData} />
        </ListPage.Header>
        <DataTable flex={1}>
            <DataTable.Content
                rows={fetchData.data?.result}
                columns={[
                    ...columnData.columns,
                    hasWritePermiso && {
                        header: "Acción", width: "auto", align: "center", render: row => <DataTable.EntryOption>
                            <DataTable.EntryOption.EditButton onClick={() => openModal("edit", row.id)} />
                            <DataTable.EntryOption.DeleteButton disabled={row.principal || row.protegido} onClick={() => openModal("delete", row)} />
                        </DataTable.EntryOption>
                    }
                ]}
                fit="flex"
                loading={fetchData.isFetching}
                error={fetchData.error}
            />
            <DataTable.Pagination filterData={filterData} queryResult={fetchData} />
        </DataTable>

        <Drawer code="add">
            <ModalHeader>Agregar Depósito</ModalHeader>
            <AddDepositoMutation>
                {mutation => <SchemaFormHook
                    schema={depositoFormSchema}
                    onSubmit={async ({ value }) => {
                        await mutation.mutateAsync(value);
                        notify("El depósito se ha agregado correctamente.");
                    }}
                    submitOnce
                    closeModalOnSubmit
                >
                    {form => <DepositoForm form={form} margin={3} />}
                </SchemaFormHook>}
            </AddDepositoMutation>
        </Drawer>

        <Drawer code="edit">
            <ModalHeader>Editar Depósito</ModalHeader>
            <ModalValueExtractor>
                {depositoId => <GetDepositoQuery id={depositoId}>
                    {query => <DataFetcher flex={1} query={query}>
                        {deposito => <EditDepositoMutation id={deposito.id}>
                            {mutation => <SchemaFormHook
                                schema={depositoFormSchema}
                                onSubmit={async ({ value }) => {
                                    await mutation.mutateAsync(value);
                                    notify("El depósito se ha editado correctamente.");
                                }}
                                defaultValues={{ ...deposito }}
                                submitOnce
                                closeModalOnSubmit
                            >
                                {form => <DepositoForm form={form} margin={3} />}
                            </SchemaFormHook>}
                        </EditDepositoMutation>}
                    </DataFetcher>}
                </GetDepositoQuery>}
            </ModalValueExtractor>
        </Drawer>

        <ConfirmDialog code="delete">
            <ModalValueExtractor>
                {deposito => <DeleteDepositoMutation id={deposito.id}>
                    {mutation => <ConfirmDialog.Content
                        title={`Borrar Depósito "${deposito.nombre}"`}
                        description={`¿Estás seguro que quieres borrar el depósito "${deposito.nombre}"?`}
                        onConfirm={async () => {
                            await mutation.mutateAsync({});
                            notify(`El depósito "${deposito.nombre}" se ha borrado correctamente.`, { variant: "success" });
                        }}
                    />}
                </DeleteDepositoMutation>}
            </ModalValueExtractor>
        </ConfirmDialog>
    </ListPage>
}
